import React, { useState } from 'react';
import { toast } from 'react-toastify';
import MagicButton from 'components/MagicButton/MagicButton';
import {
  useGetRegistrationAllowlistQuery,
  useAddRegistrationAllowlistEntryMutation,
  useRemoveRegistrationAllowlistEntryMutation,
} from 'store/services/admin/adminApi';
import { ALLOWLIST_DUPLICATE_CODE } from 'types/interfaces/admin/registrationAllowlist';
import { formatAdminDate } from 'utils/formatAdminDate';
import './RegistrationAllowlistSection.css';

const DUPLICATE_MESSAGE = 'This email is already on the allowlist.';

// A shape check only, to keep an obvious typo from costing a round trip. The
// backend validates and normalizes; it is the authority on what is accepted.
const looksLikeEmail = (value: string): boolean => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value);

/**
 * Registration allowlist (Asana 1218634285737762, Decision 14). Lets an admin
 * exempt one email from the registration blocks for 7 days, for a real person
 * caught by a rule. Keyed on email because residential proxy IPs rotate faster
 * than a support reply. Expiry is set by the backend; this section only shows it.
 */
const RegistrationAllowlistSection: React.FC = () => {
  const { data, isLoading, isError } = useGetRegistrationAllowlistQuery();
  const [addEntry, { isLoading: isAdding }] = useAddRegistrationAllowlistEntryMutation();
  const [removeEntry] = useRemoveRegistrationAllowlistEntryMutation();

  const [email, setEmail] = useState('');
  const [note, setNote] = useState('');
  const [formError, setFormError] = useState<string | null>(null);
  const [removingId, setRemovingId] = useState<number | null>(null);

  const rows = data?.rows ?? [];
  const trimmedEmail = email.trim();
  const canAdd = looksLikeEmail(trimmedEmail) && !isAdding;

  const handleAdd = async () => {
    if (!canAdd) return;
    setFormError(null);
    const trimmedNote = note.trim();
    try {
      const row = await addEntry({
        email: trimmedEmail,
        ...(trimmedNote ? { note: trimmedNote } : {}),
      }).unwrap();
      // The backend returns the normalized email, which can differ from what
      // was typed. Name the stored form so the admin sees what was saved.
      toast.success(`${row.email} added to the registration allowlist.`);
      setEmail('');
      setNote('');
    } catch (err: any) {
      if (err?.data?.code === ALLOWLIST_DUPLICATE_CODE) {
        setFormError(DUPLICATE_MESSAGE);
        return;
      }
      toast.error(err?.data?.message || 'Failed to add the email. Please try again.');
    }
  };

  const handleRemove = async (id: number) => {
    setRemovingId(id);
    try {
      await removeEntry(id).unwrap();
    } catch (err: any) {
      toast.error(err?.data?.message || 'Failed to remove the entry. Please try again.');
    } finally {
      setRemovingId(null);
    }
  };

  const renderList = () => {
    if (isLoading) return <p className="loading-text">Loading allowlist...</p>;
    if (isError) return <p className="reg-allowlist-empty">Could not load the allowlist.</p>;
    if (rows.length === 0) return <p className="reg-allowlist-empty">No active allowlist entries.</p>;

    return (
      <div className="reg-allowlist-table-wrapper">
        <table className="reg-allowlist-table">
          <thead>
            <tr>
              <th>Email</th>
              <th>Note</th>
              <th>Added (UTC)</th>
              <th>Expires (UTC)</th>
              <th>Status</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {rows.map((row) => (
              <tr key={row.id} className={row.usedAt ? 'reg-allowlist-row-used' : undefined}>
                <td className="reg-allowlist-email">{row.email}</td>
                <td className="reg-allowlist-note">{row.note || '-'}</td>
                <td>{formatAdminDate(row.createdAt)}</td>
                <td>{formatAdminDate(row.expiresAt)}</td>
                <td>
                  {row.usedAt ? (
                    <span className="reg-allowlist-status reg-allowlist-status-used">
                      Used {formatAdminDate(row.usedAt)}
                    </span>
                  ) : (
                    <span className="reg-allowlist-status reg-allowlist-status-active">Active</span>
                  )}
                </td>
                <td>
                  <button
                    className="reg-allowlist-remove-btn"
                    onClick={() => handleRemove(row.id)}
                    disabled={removingId === row.id}
                  >
                    {removingId === row.id ? 'Removing...' : 'Remove'}
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    );
  };

  return (
    <div className="controls-card reg-allowlist-card">
      <div className="card-header">
        <h2>Registration Allowlist</h2>
        <p className="card-description">
          Let one email through the registration blocks for 7 days. Use it for a real person
          caught by a rule. An entry is used up by its first successful registration.
        </p>
      </div>

      <div className="card-content">
        <div className="reg-allowlist-form">
          <input
            type="email"
            className="reg-allowlist-input reg-allowlist-input-email"
            placeholder="Email"
            aria-label="Email to allowlist"
            value={email}
            onChange={(e) => {
              setEmail(e.target.value);
              if (formError) setFormError(null);
            }}
            onKeyDown={(e) => {
              if (e.key === 'Enter') handleAdd();
            }}
          />
          <input
            type="text"
            className="reg-allowlist-input reg-allowlist-input-note"
            placeholder="Note (optional)"
            aria-label="Note"
            value={note}
            maxLength={500}
            onChange={(e) => setNote(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') handleAdd();
            }}
          />
          <MagicButton
            label={isAdding ? 'Adding...' : 'Add'}
            color="purple"
            onPress={handleAdd}
            isDisabled={!canAdd}
            fullWidth={false}
          />
        </div>
        {formError ? <p className="reg-allowlist-form-error">{formError}</p> : null}

        {renderList()}
      </div>
    </div>
  );
};

export default RegistrationAllowlistSection;
